// src/context/LanguageContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(
    () => localStorage.getItem('lang') || 'en'
  );

  const isAr = lang === 'ar';

  useEffect(() => {
    localStorage.setItem('lang', lang);
    // Set HTML dir and lang attributes for proper RTL rendering
    document.documentElement.setAttribute('dir', isAr ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
    // RTL font — Arabic needs a different font stack
    document.body.style.fontFamily = isAr
      ? "'Segoe UI', 'Tahoma', 'Arabic Typesetting', system-ui, sans-serif"
      : "'Segoe UI', system-ui, sans-serif";
  }, [lang, isAr]);

  // Core translation helper
  // Usage: t({ en: 'Hello', ar: 'مرحبا' })
  // Or:    t('Hello', 'مرحبا')
  const t = (enOrObj, ar) => {
    if (typeof enOrObj === 'object') {
      return isAr ? (enOrObj.ar || enOrObj.en) : enOrObj.en;
    }
    return isAr ? (ar || enOrObj) : enOrObj;
  };

  // Get item name in current language
  const itemName = (item) => {
    if (!item) return '';
    return isAr && item.nameAr ? item.nameAr : (item.name || item.englishName || '');
  };

  // Get item description in current language
  const itemDesc = (item) => {
    if (!item) return '';
    return isAr && item.descriptionAr ? item.descriptionAr : (item.description || item.englishDescription || '');
  };

  const toggleLang = () => setLang(l => l === 'en' ? 'ar' : 'en');

  return (
    <LanguageContext.Provider value={{ lang, isAr, t, itemName, itemDesc, toggleLang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
